/**
 * Quiz Schema
 * A comprehensive assessment that can include multiple question types
 */

import type { ContentConfig, LayoutConfig, ScoringConfig } from '@ai-content-hub/content-core';
import type {
    MCQElementData,
    TextElementData,
    ImageElementData,
    RichTextElementData,
} from '@ai-content-hub/content-elements';

/**
 * Supported element types in a quiz
 */
export type QuizElementType = 'mcq' | 'text' | 'image' | 'rich-text';

/**
 * Quiz Item - a single item in the quiz (question or content)
 */
export interface QuizItem {
    id: string;
    type: QuizElementType;
    data: MCQElementData | TextElementData | ImageElementData | RichTextElementData;
    layout?: ItemLayout;
    /** Whether this item is gradeable (questions only) */
    gradeable?: boolean;
    /** Points for this item */
    points?: number;
}

/**
 * Per-item layout override
 */
export interface ItemLayout {
    alignment?: 'left' | 'center' | 'right';
    spacing?: 'none' | 'sm' | 'md' | 'lg';
    hidden?: boolean;
}

/**
 * Quiz Section - groups items into sections
 */
export interface QuizSection {
    id: string;
    title?: string;
    description?: string;
    items: QuizItem[];
}

/**
 * Quiz Data - the content data
 */
export interface QuizData {
    title: string;
    description?: string;
    instructions?: string;
    /** Flat list of items or sectioned */
    items?: QuizItem[];
    sections?: QuizSection[];
    /** Time limit in minutes */
    timeLimit?: number;
}

/**
 * Quiz Content - complete content item
 */
export interface QuizContent {
    id: string;
    type: 'quiz';
    version: string;
    data: QuizData;
    config?: QuizConfig;
}

/**
 * Layout configuration for Quiz
 */
export interface QuizLayoutConfig extends LayoutConfig {
    /** How to display questions */
    displayMode?: 'all' | 'step' | 'sections';
    /** Show progress indicator */
    showProgress?: boolean;
    /** Show question numbers */
    showQuestionNumbers?: boolean;
    /** Show section headers */
    showSectionHeaders?: boolean;
    /** MCQ choices: standard = vertical stack (default); adaptive = horizontal grid on lg when there are fewer than five options */
    answerChoicesPresentation?: 'standard' | 'adaptive';
}

/**
 * Scoring configuration for Quiz - extends base ScoringConfig
 */
export interface QuizScoringConfig extends ScoringConfig {
    /** Total points for the quiz */
    totalPoints?: number;
    /** Passing percentage (0-100) */
    passingPercentage?: number;
    /** Show score during quiz */
    showScoreDuringQuiz?: boolean;
}

/**
 * Timer configuration for Quiz
 */
export interface QuizTimerConfig {
    enabled?: boolean;
    /** Duration in seconds */
    duration?: number;
    /** Warning at X seconds remaining */
    warningAt?: number;
    /** Auto-submit when time expires */
    autoSubmit?: boolean;
}

/**
 * Quiz Config - uses Omit to replace base types with extended versions
 */
export interface QuizConfig extends Omit<ContentConfig, 'layout' | 'scoring'> {
    layout?: QuizLayoutConfig;
    scoring?: QuizScoringConfig;
    timer?: QuizTimerConfig;
}

/**
 * Default i18n translations for Quiz
 */
export const defaultQuizI18n = {
    en: {
        // Actions
        submit: 'Submit',
        submitQuiz: 'Submit Quiz',
        retry: 'Try Again',
        next: 'Next',
        previous: 'Previous',
        // Feedback
        correct: 'Correct!',
        incorrect: 'Incorrect',
        congratulations: '🎉 Congratulations!',
        quizComplete: 'Quiz Complete',
        // Status
        timeUp: 'Time is up!',
        answerAllQuestions: 'Answer all questions to submit',
        // Labels
        progress: 'Progress',
        answered: 'answered',
        question: 'Question',
        score: 'Score',
        points: 'Points',
        timeSpent: 'Time spent',
        minutes: 'm',
        seconds: 's',
        attemptsLeft: 'attempts left',
        noQuizData: 'No quiz data',
        noQuizItems: 'No quiz items',
        quizTitlePlaceholder: 'Enter quiz title...',
        quizDescriptionPlaceholder: 'Enter description (optional)...',
        quizInstructionsPlaceholder: 'Enter instructions for the quiz taker...',
        multipleChoice: 'Multiple Choice',
        textBlock: 'Text Block',
        untitledQuestion: 'Untitled Question',
        emptyText: 'Empty text',
        unknownItem: 'Unknown item',
        unknownElementType: 'Unknown element type',
        quizDetails: 'Quiz Details',
        title: 'Title',
        description: 'Description',
        instructions: 'Instructions',
        quizItems: 'Quiz Items',
        addItem: 'Add Item',
        noItemsYet: 'No items yet. Add a question or text block.',
        add: 'Add',
        gradeable: 'Gradeable',
        pointsLabel: 'Points',
    },
    vi: {
        // Actions
        submit: 'Gửi',
        submitQuiz: 'Nộp bài',
        retry: 'Thử lại',
        next: 'Tiếp theo',
        previous: 'Trước',
        // Feedback
        correct: 'Chính xác!',
        incorrect: 'Chưa đúng',
        congratulations: '🎉 Chúc mừng!',
        quizComplete: 'Hoàn thành bài kiểm tra',
        // Status
        timeUp: 'Hết thời gian!',
        answerAllQuestions: 'Trả lời tất cả câu hỏi để nộp bài',
        // Labels
        progress: 'Tiến độ',
        answered: 'đã trả lời',
        question: 'Câu hỏi',
        score: 'Điểm',
        points: 'Điểm',
        timeSpent: 'Thời gian',
        minutes: ' phút',
        seconds: ' giây',
        attemptsLeft: 'lần thử còn lại',
        noQuizData: 'Không có dữ liệu bài kiểm tra',
        noQuizItems: 'Không có mục nào trong bài kiểm tra',
        quizTitlePlaceholder: 'Nhập tiêu đề bài kiểm tra...',
        quizDescriptionPlaceholder: 'Nhập mô tả (tuỳ chọn)...',
        quizInstructionsPlaceholder: 'Nhập hướng dẫn cho người làm bài...',
        multipleChoice: 'Trắc nghiệm',
        textBlock: 'Khối văn bản',
        untitledQuestion: 'Câu hỏi chưa có tiêu đề',
        emptyText: 'Văn bản trống',
        unknownItem: 'Mục không xác định',
        unknownElementType: 'Loại phần tử không xác định',
        quizDetails: 'Chi tiết bài kiểm tra',
        title: 'Tiêu đề',
        description: 'Mô tả',
        instructions: 'Hướng dẫn',
        quizItems: 'Các mục kiểm tra',
        addItem: 'Thêm mục',
        noItemsYet: 'Chưa có mục nào. Thêm câu hỏi hoặc khối văn bản.',
        add: 'Thêm',
        gradeable: 'Tính điểm',
        pointsLabel: 'Điểm',
    },
};

/**
 * Default Quiz Config
 */
export const defaultQuizConfig: QuizConfig = {
    layout: {
        type: 'vertical',
        gap: '2rem',
        displayMode: 'all',
        showProgress: true,
        showQuestionNumbers: true,
        showSectionHeaders: true,
    },
    behavior: {
        allowRetry: false,
        shuffleChoices: false,
        showCorrectAnswer: true,
        showExplanation: true,
        examMode: false,
    },
    scoring: {
        totalPoints: 100,
        partialCreditEnabled: false,
        passingPercentage: 70,
        showScoreDuringQuiz: false,
    },
    timer: {
        enabled: false,
        duration: 1800, // 30 minutes
        warningAt: 300, // 5 minutes warning
        autoSubmit: true,
    },
    i18n: {
        defaultLanguage: 'en',
        languages: defaultQuizI18n,
    },
};

/**
 * Default Quiz Data
 */
export const defaultQuizData: QuizData = {
    // EAA-2082: a new Quiz starts with an EMPTY title so the editor shows only the
    // placeholder. A non-empty default ("New Quiz") was picked up by the question-bank page
    // as the content title and saved although nobody typed it.
    title: '',
    description: '',
    items: [],
};

/**
 * Quiz Content Type Schema Definition
 */
export const quizSchema = {
    type: 'quiz',
    title: 'Quiz',
    description: 'A comprehensive assessment with multiple question types',
    version: '1.0.0',
    category: 'assessment',
    icon: 'FileQuestion',
    tags: ['quiz', 'assessment', 'exam', 'test'],
    defaultData: defaultQuizData,
    defaultConfig: defaultQuizConfig,
};
